import {
  formatRecurrenceDescriptor,
  isoWeekdayToRRuleDay,
  parseRecurrenceDescriptor,
  validateRecurrenceRule,
  type PositionalWeekday,
  type RecurrenceRuleDescriptor,
  type RRuleDay,
  type RRuleFrequency,
} from '@zag/calendar/models';
import { ALL_WORKDAYS_MASK, isoDaysToWorkdayMask, workdayMaskToIsoDays } from '../workday-mask';
import type { EmploymentCadence, EmploymentCadenceUnit, EmploymentRecurrence, IsoWeekday } from './employment.model';

const DAYS_PER_WEEK = 7;
const MONTHS_PER_QUARTER = 3;
const SIMPLE_RRULE_PART = /^(?:FREQ=[A-Z]+|INTERVAL=[1-9]\d*|BYDAY=(?:MO|TU|WE|TH|FR|SA|SU)(?:,(?:MO|TU|WE|TH|FR|SA|SU))*)$/;

const FREQUENCY_BY_UNIT: Record<EmploymentCadenceUnit, RRuleFrequency> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
  quarter: 'monthly',
  year: 'yearly',
};

const UNIT_BY_FREQUENCY: Partial<Record<RRuleFrequency, EmploymentCadenceUnit>> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
};

export function encodeEmploymentRecurrence(cadence: EmploymentCadence, weekdays: readonly IsoWeekday[]): string | null {
  if (cadence.unit === 'week' && cadence.interval === 1) return null;
  const byDay = workdayMaskToIsoDays(isoDaysToWorkdayMask(weekdays));
  return formatRecurrenceDescriptor({
    frequency: FREQUENCY_BY_UNIT[cadence.unit],
    interval: cadence.unit === 'quarter' ? cadence.interval * MONTHS_PER_QUARTER : cadence.interval,
    byDay: omitsByDay(cadence.unit, byDay) ? undefined : byDay.map(weekday => ({ day: isoWeekdayToRRuleDay(weekday) })),
  });
}

export function decodeEmploymentRecurrence(rrule: string | null, workdayMask: number): EmploymentRecurrence {
  if (rrule === null) return { kind: 'simple', cadence: { unit: 'week', interval: 1 } };
  const cadence = simpleCadenceOf(rrule, workdayMask);
  return cadence === null ? { kind: 'custom', rrule } : { kind: 'simple', cadence };
}

function omitsByDay(unit: EmploymentCadenceUnit, byDay: readonly IsoWeekday[]): boolean {
  return unit === 'day' && byDay.length === DAYS_PER_WEEK;
}

function simpleCadenceOf(rrule: string, workdayMask: number): EmploymentCadence | null {
  if (!hasOnlySimpleParts(rrule) || !validateRecurrenceRule(rrule).valid) return null;
  const descriptor = parseRecurrenceDescriptor(rrule);
  if (!matchesMaskWeekdays(descriptor, workdayMask)) return null;
  return cadenceOf(descriptor);
}

function hasOnlySimpleParts(rrule: string): boolean {
  return rrule.split(';').every(part => SIMPLE_RRULE_PART.test(part));
}

function matchesMaskWeekdays(descriptor: RecurrenceRuleDescriptor, workdayMask: number): boolean {
  if (descriptor.byDay === undefined) return descriptor.frequency === 'daily' && workdayMask === ALL_WORKDAYS_MASK;
  return bydayMatchesMask(descriptor.byDay, workdayMask);
}

function bydayMatchesMask(byDay: PositionalWeekday[], workdayMask: number): boolean {
  const maskDays: RRuleDay[] = workdayMaskToIsoDays(workdayMask).map(isoWeekdayToRRuleDay);
  const bydayDays = new Set(byDay.map(entry => entry.day));
  return bydayDays.size === maskDays.length && maskDays.every(day => bydayDays.has(day));
}

function cadenceOf(descriptor: RecurrenceRuleDescriptor): EmploymentCadence | null {
  const unit = UNIT_BY_FREQUENCY[descriptor.frequency];
  if (unit === undefined) return null;
  const interval = descriptor.interval ?? 1;
  if (unit === 'month' && interval % MONTHS_PER_QUARTER === 0) {
    return { unit: 'quarter', interval: interval / MONTHS_PER_QUARTER };
  }
  return { unit, interval };
}
